import { Card, Col, Row, Select, Typography } from 'antd';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Form from '../../../components/Form';
import RichEditor from '../../../components/RichEditor';
import useDiscardChangesModal from '../../../hooks/useDiscardChangesModal';
import { InterviewScriptGenerator } from '../../../sinapse/interview_script';
import {
  ExpandedInterviewScriptTemplateCollection,
  InterviewScriptTemplateTypeAllowed,
  JobPostingDetail,
  StageSubTypeKeys,
} from '../../../types';

interface InterviewStageFormProps {
  namePrefix?: (string | number)[];
  stageSubType: StageSubTypeKeys;
  isGeneratingInterviewScriptState: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
  setIsDirty: ReturnType<typeof useDiscardChangesModal>[0];
  interviewScriptTemplates: ExpandedInterviewScriptTemplateCollection[];
  generateInterviewScript?: boolean;
  jobPostingId?: JobPostingDetail['id'];
  isDescriptionEmpty?: boolean;
}

const InterviewStageForm = ({
  namePrefix = [],
  stageSubType,
  isGeneratingInterviewScriptState,
  setIsDirty,
  interviewScriptTemplates,
  generateInterviewScript,
  isDescriptionEmpty,
  jobPostingId,
}: InterviewStageFormProps) => {
  const { t } = useTranslation();

  const form = Form.useFormInstance();
  const lockInterviewScriptValue = Form.useWatch([...namePrefix, 'lock_interview_script'], form);
  const interviewScriptNameValue = Form.useWatch([...namePrefix, 'interview_script_name'], form);
  const interviewScriptTemplateValue = Form.useWatch(
    [...namePrefix, 'interview_script_template'],
    form
  );
  const [isGeneratingInterviewScript, setIsGeneratingInterviewScript] =
    isGeneratingInterviewScriptState;

  const [interviewKey] = useState<string>(`stage-interview-${new Date().valueOf()}`);

  const changeStageFormState = (value: React.SetStateAction<boolean>) => {
    if (value) {
      setIsGeneratingInterviewScript(true);
      setIsDirty(true);
    } else {
      setIsGeneratingInterviewScript(false);
    }
  };

  const changeInterviewScriptTemplate = useCallback(
    (interviewScriptTemplateId: number) => {
      if (interviewScriptTemplateId) {
        const template = interviewScriptTemplates?.find(
          (template) => template.id === interviewScriptTemplateId
        );

        if (!template) {
          return;
        }

        form.setFieldValue(['stages', ...namePrefix, 'interview_script_content'], template.content);
      }
    },
    [form, interviewScriptTemplates, namePrefix]
  );

  return (
    <Row gutter={[24, 8]}>
      <Col span={24}>
        <Typography.Title level={5} style={{ fontWeight: 'normal' }}>
          {t('Configurar entrevista')}
        </Typography.Title>
        <Typography.Paragraph>
          {t(
            'Você pode selecionar um roteiro, criar um novo do zero ou colar um roteiro personalizado para usar durante essa etapa.'
          )}
        </Typography.Paragraph>
      </Col>
      {lockInterviewScriptValue ? (
        <>
          <Col span={24}>
            <Card>
              <Form.Item label={t('Roteiro de entrevista')}>
                <Select
                  disabled
                  value={interviewScriptNameValue}
                  options={[
                    {
                      value: interviewScriptNameValue,
                      key: interviewScriptNameValue,
                    },
                  ]}
                />
              </Form.Item>
              <Form.Item name={[...namePrefix, 'interview_script_content']}>
                <RichEditor readOnly image={false} video={false} />
              </Form.Item>
            </Card>
          </Col>
        </>
      ) : (
        <>
          <Col span={24}>
            {['HR_INTERVIEW', 'MANAGER_INTERVIEW'].includes(stageSubType) &&
              !lockInterviewScriptValue &&
              generateInterviewScript && (
                <InterviewScriptGenerator
                  key={interviewKey}
                  setIsDisabledInput={changeStageFormState}
                  sourceId={jobPostingId}
                  interviewKey={interviewKey}
                  triggerEnabled={!isDescriptionEmpty}
                  enabled
                  setResult={(text) => {
                    form.setFieldValue(
                      ['stages', ...namePrefix, 'interview_script_content'],
                      text?.text.replaceAll('\n', '<br>')
                    );
                    form.setFieldValue(['stages', ...namePrefix, 'interview_script_template'], 0);
                  }}
                />
              )}
          </Col>
          <Col span={24}>
            <Card>
              <Form.Item
                label={t('Roteiro de entrevista')}
                name={[...namePrefix, 'interview_script_template']}
              >
                <Select
                  placeholder={t('Escolha o roteiro')}
                  allowClear
                  disabled={isGeneratingInterviewScript}
                  options={[
                    {
                      key: `personalizado`,
                      value: 0,
                      label: 'Personalizado',
                    },
                    ...(interviewScriptTemplates
                      ?.filter(
                        (interviewScriptTemplate) =>
                          interviewScriptTemplate.type.includes(
                            stageSubType as InterviewScriptTemplateTypeAllowed
                          ) || interviewScriptTemplate.id === interviewScriptTemplateValue
                      )
                      .map((interviewScriptTemplate) => ({
                        value: interviewScriptTemplate.id,
                        key: interviewScriptTemplate.id,
                        label: interviewScriptTemplate.name,
                      })) || {}),
                  ]}
                  onChange={changeInterviewScriptTemplate}
                />
              </Form.Item>
              <Form.Item name={[...namePrefix, 'interview_script_content']}>
                <RichEditor
                  key={`rce-stage-${namePrefix}`}
                  readOnly={isGeneratingInterviewScript}
                  image={false}
                  video={false}
                  onChange={(value: string) => {
                    const template = interviewScriptTemplates?.find(
                      (template) => template.id === parseInt(interviewScriptTemplateValue, 10)
                    );

                    if (!template) {
                      return;
                    }

                    if (template.content !== value) {
                      form.setFieldValue(['stages', ...namePrefix, 'interview_script_template'], 0);
                    }
                  }}
                />
              </Form.Item>
            </Card>
          </Col>
        </>
      )}
    </Row>
  );
};
export default InterviewStageForm;
